import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { apiFetch, ApiError } from "@/lib/api";
import {
  MRP_SKU_PLANNING_FILTER_OPTIONS,
  MRP_SKU_PLANNING_OPTIONS,
  MRP_SKU_PLANNING_SELECT_OPTIONS,
  type MrpSkuMasterRow,
  type MrpSkuPlanningFilterToken,
  type MrpSkuPlanningMode,
  buildMrpSkuDetailPath,
  MRP_SKU_CATEGORY_FILTER_NONE,
} from "@/lib/mrp";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";

const PAGE_SIZE = 50;
const DEFAULT_PLANNING_SET = new Set<MrpSkuPlanningFilterToken>(
  MRP_SKU_PLANNING_FILTER_OPTIONS.map((option) => option.value),
);

type SkuListResponse = {
  skus: MrpSkuMasterRow[];
  page: number;
  page_size: number;
  total_count: number;
};

type SkuListSortKey = "sku" | "category" | "planning" | "description" | "variant";
type SkuListSortDir = "asc" | "desc";

function planningModeLabel(mode: MrpSkuPlanningMode | null): string {
  if (mode == null) {
    return "Not set";
  }
  return MRP_SKU_PLANNING_OPTIONS.find((option) => option.value === mode)?.label ?? mode;
}

function categoryLabel(row: MrpSkuMasterRow): string {
  if (row.category != null && row.category !== "") {
    return row.category;
  }
  if (row.mv_category_id != null) {
    return `Category #${row.mv_category_id}`;
  }
  return "Not set";
}

function CategoryCell(props: {
  row: MrpSkuMasterRow;
  categoryOptions: { value: number; label: string }[];
  isEditing: boolean;
  hubWritesAllowed: boolean;
  isSaving: boolean;
  onBeginEdit: () => void;
  onCancelEdit: () => void;
  onCommit: (next: number) => void;
}): JSX.Element {
  const { row, categoryOptions, isEditing, hubWritesAllowed, isSaving, onBeginEdit, onCancelEdit, onCommit } = props;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const canEdit = hubWritesAllowed;

  const selectOptions = useMemo(() => {
    const options = [...categoryOptions];
    if (row.mv_category_id != null && !options.some((option) => option.value === row.mv_category_id)) {
      const name = row.category?.trim();
      options.unshift({
        value: row.mv_category_id,
        label: name ? `${name} (${row.mv_category_id})` : `Unknown category (${row.mv_category_id})`,
      });
    }
    return options;
  }, [categoryOptions, row.category, row.mv_category_id]);

  useEffect(() => {
    if (!isEditing) return;
    selectRef.current?.focus();
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    const onPointerDown = (event: PointerEvent): void => {
      const node = event.target as Node | null;
      if (node != null && wrapRef.current != null && !wrapRef.current.contains(node)) {
        onCancelEdit();
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [isEditing, onCancelEdit]);

  if (isEditing) {
    return (
      <div ref={wrapRef} className="min-w-0">
        <select
          ref={selectRef}
          className="h-8 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-1.5 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100"
          defaultValue={row.mv_category_id == null ? "" : String(row.mv_category_id)}
          disabled={isSaving}
          aria-label={`Category for ${row.sku}`}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw === "") return;
            const next = Number.parseInt(raw, 10);
            if (!Number.isFinite(next)) return;
            onCommit(next);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              onCancelEdit();
            }
          }}
        >
          <option value="" disabled>
            Select a category
          </option>
          {selectOptions.map((option) => (
            <option key={option.value} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={!canEdit || isSaving}
      onClick={onBeginEdit}
      title={hubWritesAllowed ? "Click to change category" : undefined}
      className={cn(
        "-mx-1 w-full min-w-0 rounded px-1 py-0.5 text-left text-sm",
        canEdit && !isSaving && "text-slate-800 hover:bg-slate-100 cursor-pointer",
        (!canEdit || isSaving) && "cursor-default text-slate-600",
        row.mv_category_id == null && (!canEdit || isSaving) && "text-slate-400",
      )}
    >
      {categoryLabel(row)}
    </button>
  );
}

function PlanningModeCell(props: {
  row: MrpSkuMasterRow;
  isEditing: boolean;
  hubWritesAllowed: boolean;
  isSaving: boolean;
  onBeginEdit: () => void;
  onCancelEdit: () => void;
  onCommit: (next: MrpSkuPlanningMode | null) => void;
}): JSX.Element {
  const { row, isEditing, hubWritesAllowed, isSaving, onBeginEdit, onCancelEdit, onCommit } = props;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const selectRef = useRef<HTMLSelectElement | null>(null);

  useEffect(() => {
    if (!isEditing) return;
    selectRef.current?.focus();
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    const onPointerDown = (event: PointerEvent): void => {
      const node = event.target as Node | null;
      if (node != null && wrapRef.current != null && !wrapRef.current.contains(node)) {
        onCancelEdit();
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [isEditing, onCancelEdit]);

  if (isEditing) {
    return (
      <div ref={wrapRef} className="min-w-0">
        <select
          ref={selectRef}
          className="h-8 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-1.5 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100"
          defaultValue={row.planning_mode ?? ""}
          disabled={isSaving}
          aria-label={`Planning mode for ${row.sku}`}
          onChange={(event) => {
            const raw = event.target.value;
            const next: MrpSkuPlanningMode | null = raw === "" ? null : (raw as MrpSkuPlanningMode);
            onCommit(next);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              onCancelEdit();
            }
          }}
        >
          {MRP_SKU_PLANNING_SELECT_OPTIONS.map((option) => (
            <option key={option.value === "" ? "__not_set__" : option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={!hubWritesAllowed || isSaving}
      onClick={onBeginEdit}
      title={hubWritesAllowed ? "Click to change planning mode" : undefined}
      className={cn(
        "-mx-1 w-full min-w-0 rounded px-1 py-0.5 text-left text-sm",
        hubWritesAllowed && !isSaving && "text-slate-800 hover:bg-slate-100",
        (!hubWritesAllowed || isSaving) && "cursor-default text-slate-600",
        hubWritesAllowed && !isSaving && "cursor-pointer",
      )}
    >
      {planningModeLabel(row.planning_mode)}
    </button>
  );
}

export function Skus() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const [searchParams] = useSearchParams();
  /** Draft value in the field; does not hit the API until submit. */
  const [searchInput, setSearchInput] = useState("");
  /** Last submitted filter (Enter or Search). */
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selectedPlanningModes, setSelectedPlanningModes] = useState<Set<MrpSkuPlanningFilterToken>>(
    () => new Set(DEFAULT_PLANNING_SET),
  );
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(() => new Set());
  const [categoryFilterSearch, setCategoryFilterSearch] = useState("");
  const [showObsolete, setShowObsolete] = useState(false);
  const [planningEditSku, setPlanningEditSku] = useState<string | null>(null);
  const [categoryEditSku, setCategoryEditSku] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<SkuListSortKey>("sku");
  const [sortDir, setSortDir] = useState<SkuListSortDir>("asc");

  const planningModeMutation = useMutation<void, ApiError, { sku: string; planning_mode: MrpSkuPlanningMode | null }>({
    mutationFn: async ({ sku, planning_mode }) => {
      await apiFetch("/api/mrp/skus", {
        method: "PATCH",
        query: { sku },
        body: { planning_mode },
      });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus", "detail", variables.sku] });
      setPlanningEditSku(null);
    },
  });

  const categoryMutation = useMutation<void, ApiError, { sku: string; mv_category_id: number }>({
    mutationFn: async ({ sku, mv_category_id }) => {
      await apiFetch("/api/mrp/skus", {
        method: "PATCH",
        query: { sku },
        body: { mv_category_id },
      });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus", "detail", variables.sku] });
      setCategoryEditSku(null);
    },
  });

  const cancelPlanningEdit = useCallback(() => {
    setPlanningEditSku(null);
  }, []);

  const cancelCategoryEdit = useCallback(() => {
    setCategoryEditSku(null);
  }, []);

  /** Legacy: `?sku=` on the list URL opens detail (replaced with `/mrp/skus/:sku`). */
  useEffect(() => {
    const raw = searchParams.get("sku");
    if (raw == null || raw === "") return;
    navigate(buildMrpSkuDetailPath(raw), { replace: true });
  }, [searchParams, navigate]);

  const allPlanningSelected = selectedPlanningModes.size === MRP_SKU_PLANNING_FILTER_OPTIONS.length;
  const planningFilterKey = allPlanningSelected
    ? "all"
    : [...selectedPlanningModes].sort().join(",");

  const categoriesQuery = useQuery<
    { product_categories: { value: number; label: string }[] },
    ApiError
  >({
    queryKey: ["mrp", "mv-product-categories"],
    queryFn: () =>
      apiFetch<{ product_categories: { value: number; label: string }[] }>(
        "/api/mrp/mv-product-categories",
      ),
    staleTime: 5 * 60_000,
  });

  const categoryOptions = useMemo(() => {
    const fromApi = (categoriesQuery.data?.product_categories ?? []).map((row) => ({
      value: String(row.value),
      label: row.label,
    }));
    return [
      { value: MRP_SKU_CATEGORY_FILTER_NONE, label: "No category" },
      ...fromApi,
    ];
  }, [categoriesQuery.data?.product_categories]);

  const categorySelectOptions = useMemo(
    () =>
      (categoriesQuery.data?.product_categories ?? []).map((row) => ({
        value: row.value,
        label: row.label,
      })),
    [categoriesQuery.data?.product_categories],
  );

  const categoriesFilterReady = categoriesQuery.isSuccess;

  useEffect(() => {
    if (!categoriesFilterReady) return;
    const allowed = new Set(categoryOptions.map((row) => row.value));
    setSelectedCategoryIds((previous) => {
      const next = new Set<string>();
      for (const id of previous) {
        if (allowed.has(id)) next.add(id);
      }
      if (next.size === previous.size && [...previous].every((id) => next.has(id))) {
        return previous;
      }
      return next;
    });
  }, [categoriesFilterReady, categoryOptions]);

  const categoryFilterKey =
    selectedCategoryIds.size === 0 ? "all" : [...selectedCategoryIds].sort().join(",");

  const skusQuery = useQuery<SkuListResponse, ApiError>({
    queryKey: [
      "mrp",
      "skus",
      {
        q: submittedQuery,
        page,
        page_size: PAGE_SIZE,
        planning: planningFilterKey,
        category: categoryFilterKey,
        show_obsolete: showObsolete,
        sort: sortColumn,
        sort_dir: sortDir,
      },
    ],
    queryFn: () =>
      apiFetch<SkuListResponse>("/api/mrp/skus", {
        query: {
          page: String(page),
          page_size: String(PAGE_SIZE),
          sort: sortColumn,
          sort_dir: sortDir,
          ...(submittedQuery.trim() ? { q: submittedQuery.trim() } : {}),
          ...(!allPlanningSelected ? { planning: [...selectedPlanningModes].sort().join(",") } : {}),
          ...(categoriesFilterReady && selectedCategoryIds.size > 0
            ? { category: [...selectedCategoryIds].sort().join(",") }
            : {}),
          ...(showObsolete ? { show_obsolete: "1" } : {}),
        },
      }),
    enabled: categoriesFilterReady,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  });

  const data = skusQuery.data;
  const rows = data?.skus ?? [];
  const totalCount = data?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * PAGE_SIZE, totalCount);

  useEffect(() => {
    if (data == null) return;
    const lastPage = Math.max(1, Math.ceil(data.total_count / data.page_size));
    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [data?.total_count, data?.page_size, page, data]);

  useEffect(() => {
    setPlanningEditSku(null);
    setCategoryEditSku(null);
  }, [page, submittedQuery, planningFilterKey, categoryFilterKey, showObsolete, sortColumn, sortDir]);

  const togglePlanningMode = (token: MrpSkuPlanningFilterToken): void => {
    setSelectedPlanningModes((previous) => {
      const next = new Set(previous);
      if (next.has(token)) {
        if (next.size <= 1) return previous;
        next.delete(token);
      } else {
        next.add(token);
      }
      return next;
    });
    setPage(1);
  };

  const onSortHeaderPress = useCallback((column: SkuListSortKey) => {
    setPlanningEditSku(null);
    setCategoryEditSku(null);
    setPage(1);
    if (sortColumn === column) {
      setSortDir((previous) => (previous === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDir("asc");
    }
  }, [sortColumn]);

  const filtersActive =
    submittedQuery.trim() !== "" ||
    !allPlanningSelected ||
    selectedCategoryIds.size > 0 ||
    showObsolete;

  const skuListColumns = useMemo((): HubDataTableColumn<MrpSkuMasterRow, SkuListSortKey>[] => {
    return [
      {
        id: "sku",
        header: "SKU",
        sortKey: "sku",
        sortValue: (row) => row.sku,
        cell: (row) => (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Link
              to={buildMrpSkuDetailPath(row.sku)}
              className="font-mono text-xs text-sky-800 underline decoration-sky-300 underline-offset-2 hover:text-sky-950"
            >
              {row.sku}
            </Link>
            {row.obsolete ? (
              <Badge tone="muted" className="shrink-0">
                Obsolete
              </Badge>
            ) : null}
          </div>
        ),
      },
      {
        id: "category",
        header: "Category",
        sortKey: "category",
        sortValue: (row) => categoryLabel(row),
        cell: (row) => (
          <CategoryCell
            row={row}
            categoryOptions={categorySelectOptions}
            isEditing={categoryEditSku === row.sku}
            hubWritesAllowed={hubWritesAllowed}
            isSaving={categoryMutation.isPending && categoryMutation.variables?.sku === row.sku}
            onBeginEdit={() => {
              setPlanningEditSku(null);
              setCategoryEditSku(row.sku);
            }}
            onCancelEdit={cancelCategoryEdit}
            onCommit={(next) => {
              if (next === row.mv_category_id) {
                setCategoryEditSku(null);
                return;
              }
              if (!hubWritesAllowed) return;
              categoryMutation.mutate({ sku: row.sku, mv_category_id: next });
            }}
          />
        ),
      },
      {
        id: "planning",
        header: "Planning",
        sortKey: "planning",
        overflow: "nowrap",
        sortValue: (row) => planningModeLabel(row.planning_mode),
        cell: (row) => (
          <PlanningModeCell
            row={row}
            isEditing={planningEditSku === row.sku}
            hubWritesAllowed={hubWritesAllowed}
            isSaving={planningModeMutation.isPending && planningModeMutation.variables?.sku === row.sku}
            onBeginEdit={() => {
              setCategoryEditSku(null);
              setPlanningEditSku(row.sku);
            }}
            onCancelEdit={cancelPlanningEdit}
            onCommit={(next) => {
              if (next === row.planning_mode) {
                setPlanningEditSku(null);
                return;
              }
              if (!hubWritesAllowed) return;
              planningModeMutation.mutate({ sku: row.sku, planning_mode: next });
            }}
          />
        ),
      },
      {
        id: "description",
        header: "Description",
        sortKey: "description",
        sortValue: (row) => row.description ?? "",
        cell: (row) => row.description ?? <span className="text-slate-400">—</span>,
      },
      {
        id: "variant",
        header: "Variant",
        sortKey: "variant",
        sortValue: (row) => row.variant ?? "",
        cell: (row) => row.variant ?? <span className="text-slate-400">—</span>,
      },
    ];
  }, [
    cancelCategoryEdit,
    cancelPlanningEdit,
    categoryEditSku,
    categoryMutation,
    categorySelectOptions,
    hubWritesAllowed,
    planningEditSku,
    planningModeMutation,
  ]);

  const listTitle =
    totalCount > 0 ? (
      <>
        SKU{" "}
        <span className="font-normal text-slate-600">
          ({rangeStart}–{rangeEnd} of {totalCount})
        </span>
      </>
    ) : (
      <>
        SKU <span className="font-normal text-slate-600">(0)</span>
      </>
    );

  const paginationControls =
    totalPages > 1 ? (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={page <= 1 || skusQuery.isFetching}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
        >
          Previous
        </Button>
        <span className="text-xs text-slate-600">
          Page {page} of {totalPages}
        </span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={page >= totalPages || skusQuery.isFetching}
          onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
        >
          Next
        </Button>
      </div>
    ) : null;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">SKU</h1>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Search</CardTitle>
        </CardHeader>
        <CardBody>
          <form
            className="flex max-w-xl flex-col gap-3 sm:flex-row sm:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              setSubmittedQuery(searchInput.trim());
            }}
          >
            <label className="min-w-0 flex-1">
              <span className="block text-xs font-medium text-slate-600">
                SKU, description, product name, or variant
              </span>
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Type a filter, then press Enter or Search"
                className="mt-1"
                autoComplete="off"
              />
            </label>
            <Button type="submit" variant="secondary" className="shrink-0">
              Search
            </Button>
          </form>
          <div className="mt-4 flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Planning mode
            </span>
            <div className="flex flex-wrap gap-2">
              {MRP_SKU_PLANNING_FILTER_OPTIONS.map((opt) => {
                const isOn = selectedPlanningModes.has(opt.value);
                return (
                  <Button
                    key={opt.value}
                    type="button"
                    size="sm"
                    variant={isOn ? "primary" : "secondary"}
                    onClick={() => togglePlanningMode(opt.value)}
                  >
                    {opt.label}
                  </Button>
                );
              })}
            </div>
            <p className="text-xs text-slate-500">At least one option must stay selected.</p>
          </div>
          {categoriesFilterReady ? (
            <div className="mt-3 flex flex-col gap-2">
              <Label htmlFor="sku-list-category-filter">Category</Label>
              <SearchableMultiSelect
                id="sku-list-category-filter"
                selectedValues={selectedCategoryIds}
                onSelectedValuesChange={(next) => {
                  setSelectedCategoryIds(next);
                  setPage(1);
                }}
                options={categoryOptions}
                searchValue={categoryFilterSearch}
                onSearchChange={setCategoryFilterSearch}
                placeholder="All categories"
                searchPlaceholder="Search categories…"
                allSelectedLabel="All categories"
                emptyMessage="No categories match."
                panelMinWidth={280}
                emptySelectionMeansAll
              />
            </div>
          ) : null}
          <div className="mt-3 flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Obsolescence
            </span>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={!showObsolete ? "primary" : "secondary"}
                onClick={() => {
                  setShowObsolete(false);
                  setPage(1);
                }}
              >
                Active only
              </Button>
              <Button
                type="button"
                size="sm"
                variant={showObsolete ? "primary" : "secondary"}
                onClick={() => {
                  setShowObsolete(true);
                  setPage(1);
                }}
              >
                Include obsolete
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title={listTitle}
        headerExtra={paginationControls}
        columns={skuListColumns}
        rows={rows}
        rowKey={(row) => row.sku}
        sortKey={sortColumn}
        sortDir={sortDir}
        onSort={onSortHeaderPress}
        loading={skusQuery.isLoading}
        loadingMessage="Loading…"
        error={
          skusQuery.error
            ? skusQuery.error.status === 404
              ? "Could not load the SKU list (the server returned not found for this request)."
              : skusQuery.error.message
            : null
        }
        empty="No rows in public.sku."
        isFiltered={filtersActive}
        filteredEmpty="No rows in public.sku match this filter."
        tableClassName="w-full table-fixed border-collapse text-sm"
        rowClassName={() => "[content-visibility:auto] [contain-intrinsic-size:2.75rem]"}
      />
      {planningModeMutation.isError ? (
        <p className="text-sm text-rose-700">{planningModeMutation.error.message}</p>
      ) : null}
      {categoryMutation.isError ? (
        <p className="text-sm text-rose-700">{categoryMutation.error.message}</p>
      ) : null}
    </div>
  );
}
